import {
  formatDistanceToNow,
  isThisYear,
  isToday,
  isYesterday,
  parseISO,
  subHours,
  format,
} from "date-fns";
import ko from "date-fns/locale/ko";
import moment from "moment";

import { safeReplaceAll } from "./replace";

const COMMON_DATETIME_REGEX =
  /^(19|20)\d\d\/(0[1-9]|1[012])\/(0[1-9]|[12][0-9]|3[01])\s([01]\d|2[0-4]):([0-5]\d):([0-5]\d)$/;
const ISO_DATETIME_REGEX =
  /^(19|20)\d\d-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])T([01]\d|2[0-4]):([0-5]\d):([0-5]\d)(.\d+Z)?$/;

export function formatFullDateTime(date: Date) {
  const year = date.getFullYear();
  const month = ("0" + (date.getMonth() + 1)).slice(-2); // 월은 0부터 시작하기 때문에 +1을 해줍니다.
  const day = ("0" + date.getDate()).slice(-2);
  const hours = ("0" + date.getHours()).slice(-2);
  const minutes = ("0" + date.getMinutes()).slice(-2);
  const seconds = ("0" + date.getSeconds()).slice(-2);

  return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
}

export const getTimeFromDateString = (date: string) => {
  const time = date.split(" ")[1];
  const timeSplit = time.split(":");
  return timeSplit[0] + ":" + timeSplit[1];
};

export const convertFullDateTimeToWithoutSecond = (date: string) => {
  const splitDate = date.split(":");
  return splitDate[0] + ":" + splitDate[1];
};

// 월.일 시:분 출력
export const convertFullDateTimeToWithoutYearSecond = (dateString: string) => {
  const date = new Date(dateString);
  const month = ("0" + (date.getMonth() + 1)).slice(-2); // 월은 0부터 시작하기 때문에 +1을 해줍니다.
  const day = ("0" + date.getDate()).slice(-2);
  const hours = ("0" + date.getHours()).slice(-2);
  const minutes = ("0" + date.getMinutes()).slice(-2);
  return `${month}.${day} ${hours}:${minutes}`;
};

export function calculateTimeDifference(date1: string, date2: string): string {
  const mDate1 = moment(date1, "YYYY/MM/DD HH:mm:ss");
  const mDate2 = moment(date2, "YYYY/MM/DD HH:mm:ss");

  if (!mDate1.isValid() || !mDate2.isValid()) {
    throw new Error(
      "Invalid date format. Please input date as YYYY/MM/DD HH:mm:ss format."
    );
  }

  const diff = moment.duration(mDate2.diff(mDate1));

  const days = diff.days();
  const hours = diff.hours();
  const minutes = diff.minutes();
  const seconds = diff.seconds();

  const array: string[] = [];
  if (days > 0) {
    array.push(`${days}일`);
  }
  if (hours > 0) {
    array.push(`${hours}시`);
  }
  if (minutes > 0) {
    array.push(`${minutes}분`);
  }
  if (seconds > 0) {
    array.push(`${seconds}초`);
  }

  return array.slice(0, 3).join(" ");
}

export const convertBirthToFullYear = (birth: string) => {
  const year = birth.slice(0, 2);
  const currentYear = new Date().getFullYear(); // Gets the current year
  const currentYY = String(currentYear).slice(2); // Gets the last two digits of the current year

  let prefix = (currentYear / 100 - 1).toFixed(0).toString();
  if (parseInt(year) <= parseInt(currentYY)) {
    prefix = (currentYear / 100).toFixed(0).toString();
  }

  return prefix + birth;
};

// ISO 날짜 형식을 입력받아서 YYYY/MM/DD HH:mm 형식으로 출력
export const displayISODate = (dateStr: string) => {
  if (COMMON_DATETIME_REGEX.test(dateStr)) {
    // 초단위 제거하고 출력
    return dateStr.slice(0, -3);
  }

  if (!dateStr || !ISO_DATETIME_REGEX.test(dateStr)) {
    console.log('적절하지 않은 날짜 값입니다.(dateStr: "' + dateStr + '")');
    return undefined;
  }
  const date = parseISO(dateStr);
  return format(date, "yyyy/MM/dd HH:mm");
};
// YYYY/MM/DD HH:mm:ss -> 현재 시간 기준 시간 차이
export const displayDate = (dateStr: string) => {
  if (!dateStr || !COMMON_DATETIME_REGEX.test(dateStr)) {
    console.log('적절하지 않은 날짜 값입니다.(dateStr: "' + dateStr + '")');
    return undefined;
  }
  const date = parseISO(dateStr.replace(/\//g, "-").replace(" ", "T"));
  const now = new Date();
  if (
    formatDistanceToNow(date, { addSuffix: true }).includes(
      "less than 30 minutes"
    )
  ) {
    return formatDistanceToNow(date, { addSuffix: true, locale: ko });
  } else if (subHours(now, 12) <= date) {
    return formatDistanceToNow(date, { addSuffix: true, locale: ko });
  } else if (isToday(date)) {
    return "오늘";
  } else if (isYesterday(date)) {
    return "어제";
  } else if (subHours(now, 24 * 7) <= date) {
    return formatDistanceToNow(date, { addSuffix: true, locale: ko });
  } else if (isThisYear(date)) {
    return format(date, "MM/dd HH:mm");
  } else {
    return dateStr; // Return the original date string for dates of previous years
  }
};

export const formatDistanceToNowWithKorean = (date: Date) => {
  const seconds = date.getTime() / 1000;
  const now = new Date().getTime() / 1000;
  const diff = seconds - now;

  const list = [];
  if (diff > 60) {
    list.push(`${Math.floor(diff / 60)}분`);
  }
  list.push(`${Math.floor(diff % 60)}초`);

  return list.join(" ");
};

export const formatTimeToString = (milliSeconds: number) => {
  const seconds = Math.floor(milliSeconds / 1000);

  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;

  const formattedMins = mins.toString().padStart(2, "0");
  const formattedSecs = secs.toString().padStart(2, "0");

  return `${formattedMins}:${formattedSecs}`;
};

export const formatDiffSecondToString = (
  diffSecond: number,
  options?: { includesSeconds?: boolean }
) => {
  const list = [];
  let remainSecond = Math.floor(diffSecond / 1000);

  if (remainSecond >= 60 * 60 * 24) {
    list.push(`${Math.floor(remainSecond / (60 * 60 * 24))}일`);
    remainSecond = remainSecond % (60 * 60 * 24);
  }
  if (remainSecond >= 60 * 60) {
    list.push(`${Math.floor(remainSecond / (60 * 60))}시간`);
    remainSecond = remainSecond % (60 * 60);
  }
  if (remainSecond >= 60) {
    list.push(`${Math.floor(remainSecond / 60)}분`);
    remainSecond = remainSecond % 60;
  }
  if (
    (options?.includesSeconds === true && remainSecond > 0) ||
    (list.length === 0 && remainSecond < 60)
  ) {
    list.push(`${remainSecond}초`);
  }

  return list.join(" ");
};

export const remainTime = (dateStr: string, afterSecond: number) => {
  if (
    !dateStr ||
    (!COMMON_DATETIME_REGEX.test(dateStr) && !ISO_DATETIME_REGEX.test(dateStr))
  ) {
    console.log('적절하지 않은 날짜 값입니다. (dateStr: "' + dateStr + '")');
    return 0;
  }

  // 입력받은 dateStr + afterSecond 후의 시간이 지금과 얼마나 차이나는 지 출력한다.
  const date =
    dateStr.includes("-") && dateStr.includes("T")
      ? new Date(parseISO(dateStr).getTime() - 9 * 60 * 60 * 1000)
      : parseISO(dateStr.replace(/\//g, "-").replace(" ", "T"));
  const now = new Date();
  const afterDate = new Date(date.getTime() + afterSecond * 1000);

  return afterDate.getTime() - now.getTime();
};

export function getMaxMonthDate(): Date {
  //최대 다음달 오늘날짜 -1 일까지 선택 가능
  const today = new Date();
  const nextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0);
  return new Date(
    nextMonth.getFullYear(),
    nextMonth.getMonth(),
    today.getDate() - 1
  );
}

export const getDateWithDayOfWeekString = (date: Date | undefined) => {
  if (date === undefined) return "";
  const dayOfWeek = new Intl.DateTimeFormat(navigator.language, {
    weekday: "long",
  }).format(date);
  return `${date.getFullYear()}년 ${
    date.getMonth() + 1
  }월 ${date.getDate()}일 ${dayOfWeek}`;
};

export const getDateString = (date: Date | null) => {
  if (date === null) return "";
  const year = date.getFullYear();
  const month = ("0" + (date.getMonth() + 1)).slice(-2); // 월은 0부터 시작하기 때문에 +1을 해줍니다.
  const day = ("0" + date.getDate()).slice(-2);
  return `${year}-${month}-${day}`;
};

export const getHourMinString = (minutes: number | undefined) => {
  if (minutes === undefined) return "";
  const hours = Math.floor(minutes / 60);
  const minutesLeft = minutes % 60;

  if (hours === 0) return `${minutesLeft}분`;
  if (minutesLeft === 0) return `${hours}시간`;
  return `${hours}시간 ${minutesLeft}분`;
};

export const getTimeString = (minutes: number | undefined) => {
  if (minutes === undefined) return "";
  const hours = Math.floor(minutes / 60);
  const minutesLeft = minutes % 60;
  if (hours === 0) return `00:${("0" + minutesLeft).slice(-2)}`;
  if (minutesLeft === 0) return `${("0" + hours).slice(-2)}:00`;
  return `${("0" + hours).slice(-2)}:${("0" + minutesLeft).slice(-2)}`;
};

export const getYearToMinuteFromISODateString = (date?: string) => {
  if (!date) return "";
  const split = date.split(".")[0].split(":");
  const withOutSecond = split[0] + ":" + split[1];
  return safeReplaceAll(safeReplaceAll(withOutSecond, "-", "/"), "T", " ");
};

export const removeSecondFromDateString = (date?: string) => {
  if (!date) return "";
  const split = date.split(":");
  return split[0] + ":" + split[1];
};

export const getDateRangeString = (start: string, end: string) => {
  if (
    !start ||
    !end ||
    !COMMON_DATETIME_REGEX.test(start) ||
    !COMMON_DATETIME_REGEX.test(end)
  ) {
    return "";
  }

  const [startDate, startTime] = start.split(" ");
  const [endDate, endTime] = end.split(" ");

  if (startDate !== endDate) {
    return (
      removeSecondFromDateString(start) +
      " ~ " +
      removeSecondFromDateString(end)
    );
  } else {
    return (
      startDate +
      " " +
      removeSecondFromDateString(startTime) +
      " ~ " +
      removeSecondFromDateString(endTime)
    );
  }
};

export const getTimeDiffString = (timestamp?: string) => {
  if (!timestamp) return "";

  const date = new Date(timestamp);
  const now = new Date();

  // 두 시간의 차이를 밀리초로 계산
  const diffMilliseconds = now.getTime() - date.getTime();

  // 밀리초를 초로 변환
  const diffSeconds = diffMilliseconds / 1000;

  // 초를 분으로 변환
  const diffMinutes = diffSeconds / 60;

  // 분을 시간으로 변환
  const diffHours = diffMinutes / 60;

  // 시간을 일로 변환
  const diffDays = diffHours / 24;

  // 일을 주로 변환
  const diffWeeks = diffDays / 7;

  // 일을 월로 변환 (1월은 평균 30.44일로 계산)
  const diffMonths = diffDays / 30.44;

  // 일을 년으로 변환
  const diffYears = diffDays / 365.25;

  let diffStr = "";
  if (diffYears >= 1) {
    diffStr = `${diffYears.toFixed(0)}년 전`;
  } else if (diffMonths >= 1) {
    diffStr = `${diffMonths.toFixed(0)}달 전`;
  } else if (diffWeeks >= 1) {
    diffStr = `${diffWeeks.toFixed(0)}주 전`;
  } else if (diffDays >= 1) {
    diffStr = `${diffDays.toFixed(0)}일 전`;
  } else if (diffHours >= 1) {
    diffStr = `${diffHours.toFixed(0)}시간 전`;
  } else if (diffMinutes >= 1) {
    diffStr = `${diffMinutes.toFixed(0)}분 전`;
  } else {
    diffStr = `${diffSeconds.toFixed(0)}초 전`;
  }

  return diffStr;
};

export const isSameDate = (date1: Date, date2: Date) => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

export const isTimePassed = (date: string, time: string) => {
  // 잘못된 포맷 입력 시 에러 throw
  if (!/\d{4}-\d{2}-\d{2}/.test(date) || !/\d{2}:\d{2}/.test(time)) {
    throw new Error("Invalid Format");
  }

  // 예약 날짜와 시간을 결합
  const reservationDateTime = `${date}T${time}:00`;

  // 예약 DateTime을 JavaScript Date 객체로 변환
  const reservationDateObject = new Date(reservationDateTime);

  // 현재 시간을 JavaScript Date 객체로 가져옴
  const currentDate = new Date();

  // 예약 시간과 현재 시간을 비교
  return currentDate > reservationDateObject;
};

export const isDateTimePassed = (
  date: string,
  compareDate: Date = new Date()
) => {
  // 잘못된 포맷 입력 시 에러 throw
  if (!/\d{4}\/\d{2}\/\d{2} \d{2}:\d{2}:\d{2}/.test(date)) {
    throw new Error("Invalid Format");
  }

  const targetDate = new Date(date);
  return targetDate < compareDate;
};
